import { useEffect, useState, useMemo } from 'react';
import { format } from 'date-fns';
import { Star, Users, User, Zap, Filter } from 'lucide-react';
import { supabase } from '../services/supabase';
import type { Database } from '../types/database.types';
import type { GroupLevel } from '../types/database';
import { listaEnCastellano } from '../lib/leerVarias';

interface Rating {
  id: string;
  user_id: string;
  session_id: string;
  rating: number;
  comment: string | null;
  created_at: string;
  alumno: string;
  session_date: string;
  grupo: string;
  tipo: 'grupal' | 'individual' | 'especial';
  nivel: string;
  monitor: string;
}

// Shapes intermedias del .map de transformación en `loadRatings`. Cada query
// pide un Pick del Row generado — escojo los campos a mano por nivel.
type SessionRatingRow = Database['public']['Tables']['session_ratings']['Row'];
type SessionRow = Database['public']['Tables']['sessions']['Row'];
type GroupRow = Database['public']['Tables']['groups']['Row'];

type RatingBrief = Pick<
  SessionRatingRow,
  'id' | 'rating' | 'comment' | 'created_at' | 'user_id' | 'session_id'
>;
type ProfileBrief = { id: string; full_name: string };
type SessionBrief = Pick<
  SessionRow,
  'id' | 'date' | 'is_special_class' | 'group_id' | 'special_class_id'
>;
type GroupBrief = Pick<GroupRow, 'id' | 'name' | 'class_type' | 'level' | 'monitor_id'>;
type GroupWithMonitor = GroupBrief & { monitor_name: string };

const TIPO_LABELS: Record<string, string> = {
  grupal: 'Grupal',
  individual: 'Individual',
  especial: 'Especial',
};

const TIPO_COLORS: Record<string, string> = {
  grupal: 'badge-gold',
  individual: 'badge-silver',
  especial: 'badge-warning',
};

const NIVEL_LABELS: Record<string, string> = {
  basico: 'Básico',
  medio: 'Medio',
  avanzado: 'Avanzado',
  tecnificacion: 'Tecnificación',
};

const NIVEL_COLORS: Record<string, string> = {
  basico: 'badge-silver',
  medio: 'badge-gold',
  avanzado: 'badge-warning',
  tecnificacion: 'badge-success',
};

// ── Estrellas ─────────────────────────────────────────────────────────────────

function Stars({ count, size = 14 }: { count: number; size?: number }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          size={size}
          className={n <= count ? 'text-gold-primary fill-gold-primary' : 'text-bg-border'}
        />
      ))}
    </div>
  );
}

function avg(arr: number[]) {
  return arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;
}

// ── Stat card ─────────────────────────────────────────────────────────────────

function StatCard({
  label,
  value,
  count,
  icon: Icon,
}: {
  label: string;
  value: number;
  count: number;
  icon: React.ElementType;
}) {
  return (
    <div className="card text-center space-y-2">
      <div className="flex justify-center text-text-secondary">
        <Icon size={20} />
      </div>
      <p className="label mb-0">{label}</p>
      <p className="font-oswald text-4xl text-gold-primary">{value.toFixed(1)}</p>
      <Stars count={Math.round(value)} />
      <p className="text-text-secondary text-xs">
        {count} valoración{count !== 1 ? 'es' : ''}
      </p>
    </div>
  );
}

// ── Página principal ──────────────────────────────────────────────────────────

// Los nombres de lo que se lee, para poder contar CUÁL no se pudo leer en vez
// de un «algo ha fallado».
const QUE_ALUMNOS = 'los nombres de los alumnos';
const QUE_SESIONES = 'las sesiones';
const QUE_GRUPOS = 'los grupos';
const QUE_ENTRENADORES = 'los entrenadores';
const QUE_ESPECIALES = 'las clases especiales';

export default function ValoracionesPage() {
  const [ratings, setRatings] = useState<Rating[]>([]);
  const [loading, setLoading] = useState(true);
  // La lectura base. Su error iba a `console.error` y la página remataba con
  // «Sin valoraciones con los filtros seleccionados», que manda a mirar donde
  // no es: la forma exacta del #496.
  const [errorCarga, setErrorCarga] = useState<string | null>(null);
  // Las cinco lecturas de enriquecimiento. Sin ellas las valoraciones siguen
  // valiendo —la nota y el comentario son lo que se viene a leer—, pero las
  // columnas caen a «—» y los dos paneles de agregados salen incompletos. Eso
  // se dice, no se pinta como un guion más.
  const [fallidos, setFallidos] = useState<string[]>([]);
  const [filterTipo, setFilterTipo] = useState('');
  const [filterNivel, setFilterNivel] = useState('');
  const [filterMonitor, setFilterMonitor] = useState('');
  const [filterSession, setFilterSession] = useState('');

  useEffect(() => {
    loadRatings();
  }, []);

  async function loadRatings() {
    setLoading(true);
    const noLeidos: string[] = [];

    // Query 1: ratings base
    const { data: rawRatings, error } = await supabase
      .from('session_ratings')
      .select('id, rating, comment, created_at, user_id, session_id')
      .order('created_at', { ascending: false });

    if (error) {
      setErrorCarga(error.message || 'Error desconocido');
      setLoading(false);
      return;
    }
    setErrorCarga(null);

    const ratingsList = (rawRatings ?? []) as RatingBrief[];
    if (!ratingsList.length) {
      setRatings([]);
      setFallidos([]);
      setLoading(false);
      return;
    }

    const sessionIds = [...new Set(ratingsList.map((r) => r.session_id))];
    const userIds = [...new Set(ratingsList.map((r) => r.user_id))];

    // Query 2: alumno names
    const { data: profilesRaw, error: errorAlumnos } = await supabase
      .from('profiles')
      .select('id, full_name')
      .in('id', userIds);
    if (errorAlumnos) noLeidos.push(QUE_ALUMNOS);
    const profileMap: Record<string, string> = Object.fromEntries(
      ((profilesRaw ?? []) as ProfileBrief[]).map((p) => [p.id, p.full_name])
    );

    // Query 3: sessions. De esta cuelgan las tres siguientes: sin ella no hay
    // grupo, ni nivel, ni entrenador, y los DOS paneles de agregados salen
    // «Sin datos» — que se lee como «nadie ha valorado», no como «no se ha
    // podido cruzar».
    const { data: sessionsRaw, error: errorSesiones } = await supabase
      .from('sessions')
      .select('id, date, is_special_class, group_id, special_class_id')
      .in('id', sessionIds);
    if (errorSesiones) noLeidos.push(QUE_SESIONES);
    const sessions = (sessionsRaw ?? []) as SessionBrief[];
    const sessionMap: Record<string, SessionBrief> = Object.fromEntries(
      sessions.map((s) => [s.id, s])
    );

    const groupIds = [
      ...new Set(sessions.filter((s) => s.group_id != null).map((s) => s.group_id as string)),
    ];
    const scIds = [
      ...new Set(
        sessions.filter((s) => s.special_class_id != null).map((s) => s.special_class_id as string)
      ),
    ];

    // Query 4: groups
    let groupMap: Record<string, GroupWithMonitor> = {};
    if (groupIds.length) {
      const { data: groupsRaw, error: errorGrupos } = await supabase
        .from('groups')
        .select('id, name, class_type, level, monitor_id')
        .in('id', groupIds);
      if (errorGrupos) noLeidos.push(QUE_GRUPOS);
      const groups = (groupsRaw ?? []) as GroupBrief[];
      const monitorIds = [
        ...new Set(groups.filter((g) => g.monitor_id != null).map((g) => g.monitor_id as string)),
      ];
      let monitorMap: Record<string, string> = {};
      if (monitorIds.length) {
        const { data: monitorsRaw, error: errorMonitores } = await supabase
          .from('profiles')
          .select('id, full_name')
          .in('id', monitorIds);
        if (errorMonitores) noLeidos.push(QUE_ENTRENADORES);
        monitorMap = Object.fromEntries(
          ((monitorsRaw ?? []) as ProfileBrief[]).map((p) => [p.id, p.full_name])
        );
      }
      groupMap = Object.fromEntries(
        groups.map((g) => [
          g.id,
          { ...g, monitor_name: g.monitor_id ? (monitorMap[g.monitor_id] ?? '—') : '—' },
        ])
      );
    }

    // Query 5: special class names
    let scMap: Record<string, string> = {};
    if (scIds.length) {
      const { data: scRaw, error: errorEspeciales } = await supabase
        .from('special_classes')
        .select('id, name')
        .in('id', scIds);
      if (errorEspeciales) noLeidos.push(QUE_ESPECIALES);
      scMap = Object.fromEntries(
        ((scRaw ?? []) as { id: string; name: string }[]).map((sc) => [sc.id, sc.name])
      );
    }

    setRatings(
      ratingsList.map((r) => {
        const sess = sessionMap[r.session_id];
        const isSpec = sess?.is_special_class ?? false;
        const group = sess?.group_id ? groupMap[sess.group_id] : null;
        return {
          id: r.id,
          user_id: r.user_id,
          session_id: r.session_id,
          rating: r.rating,
          comment: r.comment,
          created_at: r.created_at,
          alumno: profileMap[r.user_id] ?? '—',
          session_date: sess?.date ?? '',
          grupo: isSpec
            ? sess?.special_class_id
              ? (scMap[sess.special_class_id] ?? 'Clase especial')
              : 'Clase especial'
            : (group?.name ?? '—'),
          tipo: isSpec ? 'especial' : ((group?.class_type ?? 'grupal') as Rating['tipo']),
          nivel: (group?.level ?? '') as GroupLevel | '',
          monitor: group?.monitor_name ?? '—',
        };
      })
    );
    setFallidos(noLeidos);
    setLoading(false);
  }

  // Todos los monitores únicos para el filtro
  const monitors = useMemo(() => {
    const names = [...new Set(ratings.map((r) => r.monitor).filter((m) => m !== '—'))].sort();
    return names;
  }, [ratings]);

  // Niveles únicos para el filtro
  const niveles = useMemo(() => {
    const ns = [...new Set(ratings.map((r) => r.nivel).filter(Boolean))];
    return ns.sort();
  }, [ratings]);

  // Sesiones pasadas (con valoraciones) del entrenador/tipo/nivel filtrado — A1.
  // El dropdown de sesión depende de los otros filtros (sin incluirse a sí mismo).
  const sessionOptions = useMemo(() => {
    const base = ratings.filter((r) => {
      if (filterTipo && r.tipo !== filterTipo) return false;
      if (filterNivel && r.nivel !== filterNivel) return false;
      if (filterMonitor && r.monitor !== filterMonitor) return false;
      return true;
    });
    const map = new Map<string, { id: string; date: string; grupo: string; monitor: string }>();
    base.forEach((r) => {
      if (!map.has(r.session_id)) {
        map.set(r.session_id, {
          id: r.session_id,
          date: r.session_date,
          grupo: r.grupo,
          monitor: r.monitor,
        });
      }
    });
    return Array.from(map.values()).sort((a, b) => (a.date < b.date ? 1 : -1));
  }, [ratings, filterTipo, filterNivel, filterMonitor]);

  // Ratings filtrados
  const filtered = useMemo(
    () =>
      ratings.filter((r) => {
        if (filterTipo && r.tipo !== filterTipo) return false;
        if (filterNivel && r.nivel !== filterNivel) return false;
        if (filterMonitor && r.monitor !== filterMonitor) return false;
        if (filterSession && r.session_id !== filterSession) return false;
        return true;
      }),
    [ratings, filterTipo, filterNivel, filterMonitor, filterSession]
  );

  // Detalle de la sesión seleccionada (A1): media + count + comentarios.
  const sessionDetail = useMemo(() => {
    if (!filterSession) return null;
    const rs = ratings.filter((r) => r.session_id === filterSession);
    if (!rs.length) return null;
    const meta = rs[0];
    return {
      date: meta.session_date,
      grupo: meta.grupo,
      monitor: meta.monitor,
      avg: avg(rs.map((r) => r.rating)),
      count: rs.length,
      comments: rs.filter((r) => r.comment?.trim()).map((r) => r.comment as string),
    };
  }, [ratings, filterSession]);

  // Stats sobre los ratings filtrados
  const avgTotal = avg(filtered.map((r) => r.rating));

  // Por tipo de entrenamiento
  const byTipo: Record<string, number[]> = { grupal: [], individual: [], especial: [] };
  filtered.forEach((r) => {
    if (byTipo[r.tipo]) byTipo[r.tipo].push(r.rating);
  });

  // Por entrenador
  const byMonitor: Record<string, number[]> = {};
  filtered.forEach((r) => {
    if (!byMonitor[r.monitor]) byMonitor[r.monitor] = [];
    byMonitor[r.monitor].push(r.rating);
  });
  const monitorStats = Object.entries(byMonitor)
    .map(([name, rs]) => ({ name, avg: avg(rs), count: rs.length }))
    .sort((a, b) => b.avg - a.avg);

  // Por nivel
  const byNivel: Record<string, { count: number; total: number }> = {};
  filtered.forEach((r) => {
    if (!r.nivel) return;
    if (!byNivel[r.nivel]) byNivel[r.nivel] = { count: 0, total: 0 };
    byNivel[r.nivel].count++;
    byNivel[r.nivel].total += r.rating;
  });
  const nivelStats = Object.entries(byNivel)
    .map(([nivel, { count, total }]) => ({ nivel, avg: total / count, count }))
    .sort((a, b) => b.avg - a.avg);

  // Si los otros filtros dejan la sesión seleccionada fuera de las opciones, resetea.
  useEffect(() => {
    if (filterSession && !sessionOptions.some((s) => s.id === filterSession)) {
      setFilterSession('');
    }
  }, [sessionOptions, filterSession]);

  const hasFilters = filterTipo || filterNivel || filterMonitor || filterSession;

  if (loading) {
    return (
      <div className="flex justify-center h-64 items-center">
        <div className="w-8 h-8 border-2 border-gold-primary/30 border-t-gold-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (errorCarga) {
    return (
      <div className="card text-center py-12" data-testid="valoraciones-ilegibles">
        <p className="text-error text-sm">{errorCarga}</p>
        <p className="text-text-secondary text-sm mt-2">
          No dé las valoraciones por vacías ni toque los filtros: la consulta ha fallado.
        </p>
        <button
          className="btn-secondary mt-4"
          onClick={loadRatings}
          data-testid="valoraciones-reintentar"
        >
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Lo que no se pudo cruzar, dicho ARRIBA: debajo hay columnas a «—» y
          dos paneles de medias, y hay que saber cuáles no valen antes. */}
      {fallidos.length > 0 && (
        <p
          className="text-error text-sm border border-error/30 rounded px-3 py-2"
          data-testid="valoraciones-fallidos"
        >
          No se ha podido leer {listaEnCastellano(fallidos)}. Las notas y los comentarios están; lo
          que salga «—» y las medias por entrenador y por nivel, no.
        </p>
      )}

      {/* ── Filtros ── */}
      <div className="card flex flex-wrap gap-4 items-end">
        <Filter size={16} className="text-text-secondary self-center" />

        <div>
          <label className="label">Tipo de entrenamiento</label>
          <select
            className="input h-9 text-sm w-44"
            value={filterTipo}
            onChange={(e) => setFilterTipo(e.target.value)}
          >
            <option value="">Todos los tipos</option>
            <option value="grupal">Grupal</option>
            <option value="individual">Individual</option>
            <option value="especial">Especial</option>
          </select>
        </div>

        <div>
          <label className="label">Nivel del grupo</label>
          <select
            className="input h-9 text-sm w-44"
            value={filterNivel}
            onChange={(e) => setFilterNivel(e.target.value)}
          >
            <option value="">Todos los niveles</option>
            {niveles.map((n) => (
              <option key={n} value={n}>
                {NIVEL_LABELS[n] ?? n}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="label">Entrenador</label>
          <select
            className="input h-9 text-sm w-52"
            value={filterMonitor}
            onChange={(e) => setFilterMonitor(e.target.value)}
          >
            <option value="">Todos los entrenadores</option>
            {monitors.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="label">Sesión</label>
          <select
            className="input h-9 text-sm w-60"
            value={filterSession}
            onChange={(e) => setFilterSession(e.target.value)}
            data-testid="filter-session"
          >
            <option value="">Todas las sesiones</option>
            {sessionOptions.map((s) => (
              <option key={s.id} value={s.id}>
                {s.date ? format(new Date(s.date), 'dd/MM/yyyy') : '—'} · {s.grupo}
              </option>
            ))}
          </select>
        </div>

        {hasFilters && (
          <button
            onClick={() => {
              setFilterTipo('');
              setFilterNivel('');
              setFilterMonitor('');
              setFilterSession('');
            }}
            className="btn-ghost text-sm self-end"
          >
            Limpiar filtros
          </button>
        )}
      </div>

      {/* ── Detalle de la sesión seleccionada (A1) ── */}
      {sessionDetail && (
        <div className="card space-y-3" data-testid="session-detail">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-oswald text-sm uppercase tracking-wider text-white">
                Detalle de la sesión
              </h3>
              <p className="text-text-secondary text-xs">
                {sessionDetail.date ? format(new Date(sessionDetail.date), 'dd/MM/yyyy') : '—'} ·{' '}
                {sessionDetail.grupo} · {sessionDetail.monitor}
              </p>
            </div>
            <div className="text-right">
              <p className="font-oswald text-3xl text-gold-primary">
                {sessionDetail.avg.toFixed(1)}
              </p>
              <p className="text-text-secondary text-xs">
                {sessionDetail.count} valoración{sessionDetail.count !== 1 ? 'es' : ''}
              </p>
            </div>
          </div>
          <div className="border-t border-bg-border pt-3">
            <p className="label mb-2">Comentarios</p>
            {sessionDetail.comments.length === 0 ? (
              <p className="text-text-secondary text-sm">Sin comentarios en esta sesión.</p>
            ) : (
              <ul className="space-y-1" data-testid="session-comments">
                {sessionDetail.comments.map((c, i) => (
                  <li key={i} className="text-white text-sm border-l-2 border-gold-primary/40 pl-3">
                    {c}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* ── Resumen global + por tipo ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Valoración media" value={avgTotal} count={filtered.length} icon={Star} />
        <StatCard
          label="Entrenamientos grupales"
          value={avg(byTipo.grupal)}
          count={byTipo.grupal.length}
          icon={Users}
        />
        <StatCard
          label="Entrenamientos individuales"
          value={avg(byTipo.individual)}
          count={byTipo.individual.length}
          icon={User}
        />
        <StatCard
          label="Clases especiales"
          value={avg(byTipo.especial)}
          count={byTipo.especial.length}
          icon={Zap}
        />
      </div>

      {/* ── Por entrenador + por nivel ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Por entrenador */}
        <div className="card">
          <h3 className="font-oswald text-sm uppercase tracking-wider text-white mb-4">
            Valoración por entrenador
          </h3>
          {monitorStats.length === 0 ? (
            <p className="text-text-secondary text-sm">Sin datos</p>
          ) : (
            <div className="space-y-0">
              {monitorStats.map((m) => (
                <div
                  key={m.name}
                  className="flex items-center justify-between py-2.5 border-b border-bg-border last:border-0"
                >
                  <div>
                    <p className="text-white text-sm font-medium">{m.name}</p>
                    <p className="text-text-secondary text-xs">
                      {m.count} valoración{m.count !== 1 ? 'es' : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Stars count={Math.round(m.avg)} />
                    <span className="font-oswald text-gold-primary w-8 text-right">
                      {m.avg.toFixed(1)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Por nivel */}
        <div className="card">
          <h3 className="font-oswald text-sm uppercase tracking-wider text-white mb-4">
            Valoración por nivel de grupo
          </h3>
          {nivelStats.length === 0 ? (
            <p className="text-text-secondary text-sm">Sin datos</p>
          ) : (
            <div className="space-y-0">
              {nivelStats.map((n) => (
                <div
                  key={n.nivel}
                  className="flex items-center justify-between py-2.5 border-b border-bg-border last:border-0"
                >
                  <div className="flex items-center gap-3">
                    <span className={NIVEL_COLORS[n.nivel] ?? 'badge-silver'}>
                      {NIVEL_LABELS[n.nivel] ?? n.nivel}
                    </span>
                    <p className="text-text-secondary text-xs">
                      {n.count} valoración{n.count !== 1 ? 'es' : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Stars count={Math.round(n.avg)} />
                    <span className="font-oswald text-gold-primary w-8 text-right">
                      {n.avg.toFixed(1)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Tabla de valoraciones ── */}
      <div className="card p-0 overflow-hidden">
        <div className="px-6 py-3 border-b border-bg-border bg-bg-elevated flex items-center justify-between">
          <h3 className="font-oswald text-sm uppercase tracking-wider text-white">
            Valoraciones individuales
          </h3>
          <span className="text-text-secondary text-xs font-inter">
            {filtered.length} resultados
          </span>
        </div>
        <table className="w-full">
          <thead className="border-b border-bg-border bg-bg-elevated">
            <tr>
              <th className="table-th">Alumno</th>
              <th className="table-th">Grupo</th>
              <th className="table-th">Tipo</th>
              <th className="table-th">Nivel</th>
              <th className="table-th">Entrenador</th>
              <th className="table-th">Fecha</th>
              <th className="table-th">Valoración</th>
              <th className="table-th">Comentario</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={8} className="table-td text-center text-text-secondary py-12">
                  Sin valoraciones con los filtros seleccionados
                </td>
              </tr>
            ) : (
              filtered.map((r) => (
                <tr key={r.id} className="table-row">
                  <td className="table-td font-medium">{r.alumno}</td>
                  <td className="table-td text-text-secondary text-sm">{r.grupo}</td>
                  <td className="table-td">
                    <span className={TIPO_COLORS[r.tipo] ?? 'badge-gold'}>
                      {TIPO_LABELS[r.tipo] ?? r.tipo}
                    </span>
                  </td>
                  <td className="table-td">
                    {r.nivel ? (
                      <span className={NIVEL_COLORS[r.nivel] ?? 'badge-silver'}>
                        {NIVEL_LABELS[r.nivel] ?? r.nivel}
                      </span>
                    ) : (
                      <span className="text-text-secondary text-xs">—</span>
                    )}
                  </td>
                  <td className="table-td text-text-secondary text-sm">{r.monitor}</td>
                  <td className="table-td text-text-secondary text-xs">
                    {r.session_date ? format(new Date(r.session_date), 'dd/MM/yyyy') : '—'}
                  </td>
                  <td className="table-td">
                    <Stars count={r.rating} />
                  </td>
                  <td className="table-td text-text-secondary text-sm max-w-xs truncate">
                    {r.comment ?? '—'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
